import { useContext, useMemo } from 'react';
import { LocaleContext } from '../context/LocaleContext';
import { localizationService } from '../services/localizationService';

/**
 * Region-aware `Intl` formatters bound to the active language + region.
 *
 * With language `am` and region `ET`:
 *   formatLongDate(now)      → "ሰኞ 13 ጁላይ 2026"
 *   formatShortDate(now)     → "13/07/2026"          (DD/MM/YYYY)
 *   formatTime(now)          → "14:30"               (24-hour)
 *   formatNumber(1234567.89) → "1,234,567.89"
 *   formatCurrency(1234.5)   → "ብር 1,234.50"         (ETB)
 */
export function useLocale() {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error('useLocale must be used within a <LocaleProvider>');
  const { language, region, regionConfig, setRegion, setLanguage } = ctx;

  return useMemo(
    () => ({
      language,
      region,
      regionConfig,
      setLanguage,
      setRegion,
      firstDayOfWeek: regionConfig.firstDayOfWeek,
      formatNumber: (value: number, options?: Intl.NumberFormatOptions) =>
        localizationService.formatNumber(value, language, region, options),
      formatCurrency: (value: number, currency?: string) =>
        localizationService.formatCurrency(value, language, region, currency),
      formatShortDate: (date: Date) => localizationService.formatShortDate(date, region),
      formatMediumDate: (date: Date) => localizationService.formatMediumDate(date, language, region),
      formatLongDate: (date: Date) => localizationService.formatLongDate(date, language, region),
      formatTime: (date: Date) => localizationService.formatTime(date, language, region),
    }),
    [language, region, regionConfig, setLanguage, setRegion],
  );
}
